import React, { useEffect, useRef, useState } from 'react';
import locations from './locationsData';

const LOGO_URL = 'https://afag.imgix.net/sushi-yu/logo-round.png?w=100&auto=format';

const LIGHT_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', stylers: [{ color: '#b8b8b8' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', stylers: [{ color: '#c9c9c9' }] },
];

const DARK_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#1f1f1f' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#cfcfcf' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1f1f1f' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', stylers: [{ color: '#2a2a2a' }] },
  { featureType: 'water', stylers: [{ color: '#111317' }] },
];

// --- Simple HTML overlay for a "chip" label ---
function createLabelOverlay({ map, position, text, isDark, x = 0, y = 46 }) {
  // x,y are pixel offsets relative to the marker position.
  function LabelOverlay() {}
  LabelOverlay.prototype = new window.google.maps.OverlayView();

  LabelOverlay.prototype.onAdd = function () {
    this.div = document.createElement('div');
    this.div.style.position = 'absolute';
    this.div.style.transform = 'translate(-50%, 0)'; // center horizontally
    this.div.style.pointerEvents = 'none'; // don't block map gestures
    this.updateTheme(isDark);
    this.setText(text);

    const panes = this.getPanes();
    panes.overlayMouseTarget.appendChild(this.div);
  };

  LabelOverlay.prototype.updateTheme = function (dark) {
    const bg = dark ? '#111214' : '#ffffff';
    const fg = dark ? '#f4f4f5' : '#1f2328';
    const br = dark ? '#2b2f36' : '#e5e7eb';
    const shadow = dark ? '0 4px 14px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.18)';

    if (!this.div) return;
    this.div.style.background = bg;
    this.div.style.color = fg;
    this.div.style.border = `1px solid ${br}`;
    this.div.style.boxShadow = shadow;
    this.div.style.padding = '6px 10px';
    this.div.style.borderRadius = '9999px';
    this.div.style.fontFamily = 'venice-blvd, system-ui, -apple-system, Segoe UI, Roboto, Arial';
    this.div.style.fontSize = '13px';
    this.div.style.fontWeight = '700';
    this.div.style.whiteSpace = 'nowrap';

    // small caret
    this.div.style.setProperty('--caret', dark ? '#111214' : '#ffffff');
    this.div.style.setProperty('--caret-border', dark ? '#2b2f36' : '#e5e7eb');
    this.div.style.position = 'absolute';
    this.div.style.zIndex = '1';
    this.div.style.marginTop = '6px'; // a little breathing room

    if (!this.caret) {
      this.caret = document.createElement('div');
      this.caret.style.position = 'absolute';
      this.caret.style.left = '50%';
      this.caret.style.transform = 'translateX(-50%)';
      this.caret.style.top = '-6px';
      this.caret.style.width = '0';
      this.caret.style.height = '0';
      // triangle with border to match chip
      this.caret.style.borderLeft = '6px solid transparent';
      this.caret.style.borderRight = '6px solid transparent';
      this.caret.style.borderBottom = '6px solid var(--caret)';
      // create a border effect
      const borderTri = document.createElement('div');
      borderTri.style.position = 'absolute';
      borderTri.style.left = '-6px';
      borderTri.style.top = '-1px';
      borderTri.style.width = '0';
      borderTri.style.height = '0';
      borderTri.style.borderLeft = '6px solid transparent';
      borderTri.style.borderRight = '6px solid transparent';
      borderTri.style.borderBottom = '7px solid var(--caret-border)';
      this.caret.appendChild(borderTri);
      this.div.appendChild(this.caret);
    } else {
      this.caret.style.borderBottomColor = 'var(--caret)';
      this.caret.firstChild.style.borderBottomColor = 'var(--caret-border)';
    }
  };

  LabelOverlay.prototype.setText = function (t) {
    if (!this.div) return;
    this.div.textContent = t;
    this.div.appendChild(this.caret); // keep caret last
  };

  LabelOverlay.prototype.draw = function () {
    const projection = this.getProjection();
    if (!projection) return;

    const pos = projection.fromLatLngToDivPixel(position);
    if (!pos) return;

    // Position label: by default, place just BELOW the logo (~y=46px)
    this.div.style.left = `${pos.x + x}px`;
    this.div.style.top = `${pos.y + y}px`;
  };

  LabelOverlay.prototype.onRemove = function () {
    if (this.div && this.div.parentNode) {
      this.div.parentNode.removeChild(this.div);
    }
    this.div = null;
  };

  const overlay = new LabelOverlay();
  overlay.setMap(map);
  return overlay;
}

function RestaurantMap({ theme = 'system' }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);     // [{ marker, overlay }]
  const [mapReady, setMapReady] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    if (theme === 'dark') return true;
    if (theme === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Google Maps ready
  useEffect(() => {
    const handleGoogleMapsReady = () => setMapReady(true);
    if (window.google && window.google.maps) {
      handleGoogleMapsReady();
    } else {
      document.addEventListener('google-maps-ready', handleGoogleMapsReady);
      return () => document.removeEventListener('google-maps-ready', handleGoogleMapsReady);
    }
  }, []);

  // Theme watcher
  useEffect(() => {
    if (theme === 'system') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      const update = () => setIsDark(mql.matches);
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    } else {
      setIsDark(theme === 'dark');
    }
  }, [theme]);

  // Init map + add markers/overlays
  useEffect(() => {
    if (!mapReady || !containerRef.current || mapRef.current) return;

    const map = new window.google.maps.Map(containerRef.current, {
      styles: isDark ? DARK_STYLE : LIGHT_STYLE,
      backgroundColor: isDark ? '#0f0f0f' : '#ffffff',
      gestureHandling: 'greedy',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    mapRef.current = map;

    // Fit bounds / center
    if (locations.length === 1) {
      map.setCenter({ lat: locations[0].lat, lng: locations[0].lng });
      map.setZoom(13);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach((l) => bounds.extend({ lat: l.lat, lng: l.lng }));
      map.fitBounds(bounds, { padding: 80 });
    }

    // Add markers (logo) + label overlays
    markersRef.current = locations.map(({ label, lat, lng, mapLogo }) => {
      const iconSize = 40;
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        title: label,
        // No label on the Marker itself; we render a separate HTML overlay "chip"
        icon: {
          url: mapLogo || LOGO_URL,
          scaledSize: new window.google.maps.Size(iconSize, iconSize),
          // Anchor at the bottom center of the logo so the logo sits *above* the exact point
          anchor: new window.google.maps.Point(iconSize / 2, iconSize),
          // Optional: define labelOrigin if you later re-enable Marker labels
          labelOrigin: new window.google.maps.Point(iconSize / 2, iconSize + 14),
        },
      });

      // Place the chip just below the logo (y offset ~ +46px from the map point)
      const overlay = createLabelOverlay({
        map,
        position: new window.google.maps.LatLng(lat, lng),
        text: label,
        isDark,
        x: 0,
        y: 0, // chip appears right under the logo
      });

      return { marker, overlay };
    });
  }, [mapReady]);

  // Update theme (map + overlays)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.setOptions({
      styles: isDark ? DARK_STYLE : LIGHT_STYLE,
      backgroundColor: isDark ? '#0f0f0f' : '#ffffff',
    });

    // Update overlay chip styles to match theme
    markersRef.current.forEach(({ overlay }) => {
      if (overlay && overlay.updateTheme) overlay.updateTheme(isDark);
    });
  }, [isDark]);

  return (
    <div style={{ width: '100%', height: '500px' }}>
      {mapReady ? (
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '16px',
            overflow: 'hidden',
            background: isDark ? '#0f0f0f' : '#fff',
          }}
          aria-label="Restaurant locations map"
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            textAlign: 'center',
            paddingTop: '200px',
            background: isDark ? '#0f0f0f' : '#eee',
            color: isDark ? '#ccc' : '#333',
            borderRadius: '16px',
            overflow: 'hidden',
          }}
        >
          Loading map...
        </div>
      )}
    </div>
  );
}

export default RestaurantMap;